import { useState, useCallback } from 'react';
import { useQuery } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { View } from 'foundations-components/components/View';
import { CONTACT_ID, UNKNOWN_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import { SIDE_PANEL_WIDTH } from 'automation-ui-core/constants/ui';
import { DataSourcesProvider } from 'automation-ui-formula-editor/data-sources/DataSourcesContext';
import { DataPanelVisibleStates } from 'automation-ui-formula-editor/data-passing-panel/DataPassingContext';
import DataPassingPanel from 'automation-ui-formula-editor/data-passing-panel/DataPassingPanel';
import DataPassingProviderWrapper from 'automation-ui-canvas/containers/DataPassingProviderWrapper';
import TriggerType from 'automation-ui-canvas/create/TriggerType';
import { getSteps } from 'automation-ui-canvas/create/steps/utils/getSteps';
import { GET_CUSTOM_WORKFLOW_PROPERTIES } from 'automation-ui-canvas/queries/workflowFormCreatorQueries';
import CreateFlowButton from 'automation-ui-canvas/create/CreateFlowButton';
import { applyTriggerOptionToFlow, removeTriggerOptionFromFlow } from 'automation-ui-canvas/create/utils/changeTriggerOption';
import CreateStepIndicator from 'automation-ui-canvas/create/CreateStepIndicator';
import ActionConfigCombinedHeaderContainer from 'automation-ui-action-features/components/ActionConfigCombinedHeaderContainer';
import { OBJECTLESS_WORKFLOWS } from 'automation-ui-canvas/constants/Gates';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default function CreatePanelContent({
  flow,
  setFlow
}) {
  var _workflowPropertiesDa, _customProperties$hs_;
  const [internalActionConfigDataPanelDisplay, setActionConfigDataPanelDisplay] = useState(DataPanelVisibleStates.CLOSED);
  const [selectedTriggerOption, setSelectedTriggerOption] = useState(null);
  const [validationMessage, setValidationMessage] = useState(null);
  const isUngatedForObjectlessWorkflows = useIsUngated(OBJECTLESS_WORKFLOWS);
  const {
    data: workflowPropertiesData,
    loading: loadingWorkflowProperties
  } = useQuery(GET_CUSTOM_WORKFLOW_PROPERTIES);
  const workflowProperties = (_workflowPropertiesDa = workflowPropertiesData === null || workflowPropertiesData === void 0 ? void 0 : workflowPropertiesData.customWorkflowProperties) !== null && _workflowPropertiesDa !== void 0 ? _workflowPropertiesDa : [];
  const hasCustomProperties = workflowProperties.length > 0;
  const [customProperties, setCustomProperties] = useState({});
  const {
    objectTypeId
  } = flow;
  const setObjectTypeId = useCallback(newObjectTypeId => {
    setFlow(draftFlow => {
      draftFlow.objectTypeId = newObjectTypeId;
      if (newObjectTypeId === CONTACT_ID) {
        draftFlow.classicEnrollmentSettings = {};
        draftFlow.isClassicWorkflow = true;
      } else {
        delete draftFlow.classicEnrollmentSettings;
        draftFlow.isClassicWorkflow = false;
      }
    });
  }, [setFlow]);
  const actionConfigDataPanelDisplay = objectTypeId === UNKNOWN_ID ? DataPanelVisibleStates.CLOSED : internalActionConfigDataPanelDisplay;
  const [stepIndex, setStepIndex] = useState(0);
  const handleSelectTriggerOption = useCallback(triggerOption => {
    if ('supportedObjectTypeIds' in triggerOption) {
      var _triggerOption$suppor, _triggerOption$suppor2;
      setObjectTypeId((_triggerOption$suppor = (_triggerOption$suppor2 = triggerOption.supportedObjectTypeIds) === null || _triggerOption$suppor2 === void 0 ? void 0 : _triggerOption$suppor2[0]) !== null && _triggerOption$suppor !== void 0 ? _triggerOption$suppor : objectTypeId);
    }
    setSelectedTriggerOption(triggerOption);
    setFlow(draftFlow => applyTriggerOptionToFlow(triggerOption, draftFlow));
  }, [setObjectTypeId, objectTypeId, setSelectedTriggerOption, setFlow]);
  const handleClearTriggerOption = useCallback(() => {
    setStepIndex(0);
    setSelectedTriggerOption(null);
    if (selectedTriggerOption) {
      setFlow(draftFlow => removeTriggerOptionFromFlow(selectedTriggerOption, draftFlow));
    }
  }, [setStepIndex, setSelectedTriggerOption, selectedTriggerOption, setFlow]);
  const goToPreviousStep = useCallback(() => {
    if (stepIndex === 0) {
      handleClearTriggerOption();
      setObjectTypeId(UNKNOWN_ID);
    } else {
      setStepIndex(index => index - 1);
    }
    setValidationMessage(null);
  }, [stepIndex, handleClearTriggerOption, setObjectTypeId, setStepIndex, setValidationMessage]);
  const goToNextStep = useCallback(() => {
    setStepIndex(index => index + 1);
  }, [setStepIndex]);
  let steps = [];
  if (selectedTriggerOption !== null) {
    steps = getSteps({
      selectedTriggerOption,
      objectTypeId,
      isUngatedForObjectlessWorkflows,
      hasCustomProperties
    });
  }

  // Some steps have no visual component, so stepIndex doesn't account for them
  const firstStepIndex = steps.findIndex(({
    component
  }) => component);
  const effectiveStepIndex = firstStepIndex + stepIndex;
  const step = steps[effectiveStepIndex];
  const isLastStep = steps.length > 0 && effectiveStepIndex === steps.length - 1;
  const StepComponent = step === null || step === void 0 ? void 0 : step.component;
  let validationTooltip = validationMessage;
  if (selectedTriggerOption === null) {
    validationTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "createWorkflow.selectRequiredTriggerTooltip"
    });
  }
  const isValid = !validationTooltip;
  return /*#__PURE__*/_jsx(DataSourcesProvider, {
    flowId: NaN,
    flowObjectTypeId: objectTypeId,
    getActionHeaderTitle: () => null,
    children: /*#__PURE__*/_jsx(DataPassingProviderWrapper, {
      actionConfigDataPanelDisplay: actionConfigDataPanelDisplay,
      onChangeActionConfigDataPanelDisplay: setActionConfigDataPanelDisplay,
      flowId: NaN,
      flowObjectTypeId: objectTypeId,
      isFlowEnabled: false,
      disableAddDataSource: true,
      children: /*#__PURE__*/_jsxs(Flex, {
        blockSize: "100%",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          blockSize: "100%",
          flexBasis: `${SIDE_PANEL_WIDTH}px`,
          flexShrink: "0",
          overflow: "hidden",
          children: [/*#__PURE__*/_jsx(Box, {
            shrink: 0,
            children: /*#__PURE__*/_jsxs(ActionConfigCombinedHeaderContainer, {
              children: [/*#__PURE__*/_jsx(Text, {
                as: "h5",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "createWorkflow.startHeader"
                })
              }), /*#__PURE__*/_jsxs("div", {
                children: [selectedTriggerOption ? /*#__PURE__*/_jsxs(UIButton, {
                  use: "tertiary-light",
                  size: "small",
                  onClick: goToPreviousStep,
                  children: [/*#__PURE__*/_jsx(UIIcon, {
                    name: "left",
                    size: "xxs"
                  }), /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "createWorkflow.backButton"
                  })]
                }) : /*#__PURE__*/_jsx("div", {}), /*#__PURE__*/_jsx(UITooltip, {
                  title: validationTooltip,
                  disabled: isValid,
                  children: isLastStep && !loadingWorkflowProperties ? /*#__PURE__*/_jsx(CreateFlowButton, {
                    use: "tertiary"
                    // For some reason TypeScript thinks UILoadingButtonProps['size'] is number when it's clearly string
                    ,
                    size: 'small',
                    disabled: !isValid,
                    variables: {
                      flow: Object.assign({}, flow, {
                        customProperties
                      }),
                      businessUnitId: (_customProperties$hs_ = customProperties.hs_all_assigned_business_unit_ids) !== null && _customProperties$hs_ !== void 0 ? _customProperties$hs_ : undefined
                    }
                  }) : /*#__PURE__*/_jsxs(UILoadingButton, {
                    use: "tertiary",
                    size: "small",
                    disabled: !isValid,
                    onClick: goToNextStep,
                    loading: isLastStep && loadingWorkflowProperties,
                    failed: true // disables success animation
                    ,
                    children: [/*#__PURE__*/_jsx(FormattedMessage, {
                      message: "createWorkflow.nextButton"
                    }), /*#__PURE__*/_jsx(UIIcon, {
                      name: "right",
                      size: "xxs"
                    })]
                  })
                })]
              })]
            })
          }), /*#__PURE__*/_jsx(UIPanelBody, {
            className: "p-top-6 p-x-8",
            children: selectedTriggerOption && StepComponent ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(CreateStepIndicator, {
                stepIndex: effectiveStepIndex,
                steps: steps
              }), /*#__PURE__*/_jsx(StepComponent, {
                flow: flow,
                objectTypeId: objectTypeId,
                onSelectTriggerOption: handleSelectTriggerOption,
                selectedTriggerOption: selectedTriggerOption,
                setFlow: setFlow,
                setObjectTypeId: setObjectTypeId,
                setValidationMessage: setValidationMessage,
                workflowProperties: workflowProperties,
                customProperties: customProperties,
                setCustomProperties: setCustomProperties,
                goToNextStep: goToNextStep
              })]
            }) : /*#__PURE__*/_jsx(TriggerType, {
              onSelectTriggerOption: handleSelectTriggerOption,
              objectTypeId: objectTypeId,
              setObjectTypeId: setObjectTypeId
            })
          })]
        }), actionConfigDataPanelDisplay !== DataPanelVisibleStates.CLOSED && /*#__PURE__*/_jsx(View, {
          position: "relative",
          backgroundColor: "fill-surface-default",
          borderInlineStartWidth: "100",
          borderInlineStartStyle: "solid",
          borderInlineStartColor: "border-core-default",
          children: /*#__PURE__*/_jsx(DataPassingPanel, {})
        })]
      })
    })
  });
}